import { useMemo } from "react";
import { bool, number, object, string } from "prop-types";
import { observer } from "mobx-react";
import { isSafari } from "react-device-detect";
import { PriceFormats, TaskTypes, MinAvgMaxKeys } from "@repo/utils";
import { MAX_VALUES } from "@utils";
import {
  useBreakdownValueAlert,
  useEstimateEditorSettings,
  useEstimateStructure,
  useEstimateTableSettings,
  useVisibilityModeCellClassName,
  useEstimateEditorStore,
  DifferentValueAlert,
  ValueCell,
  BreakdownPriceBufferTooltip,
  TaskFixedPriceCellSettings,
} from "@tools";
import Grid from "@material-ui/core/Grid";
import classnames from "classnames";

export const TaskPriceCell = observer(({
  element,
  isSectionRow,
  expanded,
  columnSettings,
  extendPadding,
  tableDepth,
  colName,
}) => {
  const { allowEdition, isLibrary, showPdfCover } = useEstimateEditorSettings();
  const { materialsSection, showCombinedHours } = useEstimateTableSettings();
  const { visibilityMode } = useEstimateEditorStore();
  const structure = useEstimateStructure();
  const className = useVisibilityModeCellClassName(columnSettings.hide);
  
  const { estimateValueLevel, showFixedDecimalPrices } = structure;
  
  const {
    currencyObj,
    roundPrice,
    priceFormat,
    showBreakdownParentValues,
    maxValuesAsBuffer,
  } = structure.settings;
  
  const {
    lvl,
    type,
    displayPrice,
    displayPriceMax,
    displayUnbufferedPrice,
    displayUnbufferedPriceMax,
    hasChildren,
    parentHoldsFixedPrice,
    hasFixedPrice,
    hasMaterialChildren,
    usedBranchBuffers,
    displayPriceTwoValues,
  } = element;
  
  const canEdit = allowEdition && !isLibrary;
  const holdsSomeValues = Boolean(Object.entries(element.holdsViewedHours).length);
  
  const displayCellContent = lvl <= estimateValueLevel && (!expanded || !hasChildren || holdsSomeValues || hasFixedPrice || lvl === estimateValueLevel);
  const blurDisplayedValue = lvl <= estimateValueLevel && showBreakdownParentValues && !displayCellContent;
  const addMargin = type === TaskTypes.MATERIAL && !hasMaterialChildren && !materialsSection && !showCombinedHours;
  
  // const parentHoldsAllValues = parentHoldsFixedPrice && !allowEdition && displayAnyValue;
  // || Boolean(visibleWorkTypes.length && Object.entries(element.parentHoldsHours).length === visibleWorkTypes.length);
  
  const displayContent = (displayCellContent || blurDisplayedValue) && (allowEdition || !parentHoldsFixedPrice);// && lvl <= estimateValueLevel)
  
  const showMaxValue = useMemo(() => (
    (!maxValuesAsBuffer || type === TaskTypes.WORKLOAD) && displayPriceTwoValues && ((allowEdition && !maxValuesAsBuffer) || displayPrice !== displayPriceMax)
  ), [ displayPriceTwoValues, type, allowEdition, displayPrice, displayPriceMax, maxValuesAsBuffer ]);
  
  const displayUsedBuffers = useMemo(() => (
    displayCellContent && allowEdition && displayPrice && type === TaskTypes.WORKLOAD && element.hasUsedWorkTypeBuffers()
  ), [usedBranchBuffers, allowEdition, displayPrice, type, displayCellContent]);
  
  const applyPin = useMemo(() => (
    columnSettings.applyPin && (!isSafari || !showPdfCover)
  ), [ columnSettings.applyPin, isSafari, showPdfCover ]);
  
  const handlePriceChange = (price, isMax) => {
    let key = MinAvgMaxKeys.AVG;
    if(isMax)
      key = MinAvgMaxKeys.MAX;
    else if(!maxValuesAsBuffer && showMaxValue)
      key = MinAvgMaxKeys.MIN;
    
    if(!element.hasFixedPrice && element.childrenHaveFixedPrice) {
      handleValueAlertOpen({ key, price });
      return true;
    }
    else setPrice({ key, price }, hasChildren);
  };
  
  const setPrice = ({ key, price }, clearChildren) => {
    element.setFixedPrice({ [key]: price }, clearChildren, clearChildren, true);
  }
  
  const {
    valueAlertVisible,
    handleValueAlertClose,
    handleValueAlertOpen,
  } = useBreakdownValueAlert(setPrice);
  
  return (
    <ValueCell
      colName={colName}
      useDynamicWidth
      isSticky={applyPin}
      allowOverflowDisplay={applyPin}
      value={displayPrice}
      valueMax={displayPriceMax}
      placeholderMin={displayUnbufferedPrice}
      placeholderMax={displayUnbufferedPriceMax}
      min={-MAX_VALUES.PRICE}
      max={MAX_VALUES.PRICE}
      emptyInputs={parentHoldsFixedPrice}
      editable={maxValuesAsBuffer ? { min: canEdit } : canEdit}
      onValueChange={handlePriceChange}
      displayMax={showMaxValue}
      useInputBackground={!isSectionRow}
      usePriceFormat fullWidthInputs
      displayContent={displayContent}
      showDecimals={!roundPrice}
      fixedDecimalScale={showFixedDecimalPrices}
      symbolStart={currencyObj.symbolStart}
      symbolEnd={currencyObj.symbolEnd}
      textPresetClass={ isSectionRow ? "sectionTitle" : "preset-price" }
      extendPadding={extendPadding || allowEdition || visibilityMode}
      // usePresetClass={!isSectionRow}
      // useSectionClass={isSectionRow}
      allowNegative
      applySuffixSpacing
      addInnerMargin={addMargin}
      americanFormat={priceFormat === PriceFormats.AMERICAN}
      className={className}
      innerClassName={classnames(
        allowEdition && "relative",
        blurDisplayedValue && "transparent-2"
      )}
      inputClassName="input-price"
      navigationKey="price"
      
      inputMinTooltipMessage={
        displayUsedBuffers && !maxValuesAsBuffer &&
        <BreakdownPriceBufferTooltip
          element={element}
          cleanValue={displayUnbufferedPrice}
        />}
      inputMaxTooltipMessage={
        displayUsedBuffers &&
        <BreakdownPriceBufferTooltip
          element={element}
          cleanValue={displayUnbufferedPriceMax}
          isMax
        />}
    >
      {
        allowEdition && displayCellContent &&
        <Grid
          item container
          direction="column"
          justifyContent="center"
          alignItems="center"
          className="absolute w-max"
          style={{ right: -16 }}
        >
          {
            !isLibrary && type === TaskTypes.FIXED_PRICE &&
            <TaskFixedPriceCellSettings
              element={element}
              tableDepth={tableDepth}
            />
          }
        </Grid>
      }
      {
        allowEdition && valueAlertVisible &&
        <Grid
          item container
          direction="column"
          justifyContent="center"
          alignItems="center"
          className="absolute w-max"
          style={{ right: -16 }}
        >
          <DifferentValueAlert
            moduleLevel={lvl}
            handleValueAlertClose={handleValueAlertClose}
          />
        </Grid>
      }
    </ValueCell>
  )
});

TaskPriceCell.propTypes = {
  element: object.isRequired,
  columnSettings: object.isRequired,
  colName: string.isRequired,
  isSectionRow: bool,
  expanded: bool,
  tableDepth: number,
  extendPadding: bool,
};